import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Dropzone from 'react-dropzone-uploader';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@hid-galaxy-ui/galaxy-react/components/Button';
import BackupIcon from '@material-ui/icons/Backup';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import { useTranslation } from 'react-i18next';
import Api from '../../../api';
import ENDPOINTS, { ENDPOINTS_SEGMENTS } from '../../../constants/endpoints';
import { projectIdSelector, tenantIdSelector } from '../../../store/selectors/formSessionSelectors';
import { PHOTO_VALIDATION_HTTP_STATUS, PHOTO_VALIDATION_MODE } from '../../../constants/photoValidation';
import { usePhotoValidation } from '../../../hooks/usePhotoValidation';
import PhotoValidationModal from '../../PhotoValidationModal';
import { PhotoRequirementsTrigger } from '../../PhotoRequirementsDialog';
import { ERROR_TYPES } from '../../../constants/errorTypes';
import sanitizer from '../../../services/sanitize-service';
import FilePreview from '../../FilePreview/FilePreview';
import WebcamCapture from '../../WebcamCapture/WebcamCapture';
import {
  FILE_UPLOAD_STATUSES,
  FILE_UPLOAD_DIRNAME,
  FILE_UPLOAD_MAX_SIZE_BYTES,
} from '../InputFileUploadSingle/file-upload-constants';
import { useStyles } from '../InputFileUploadSingle/useStyles';
import 'react-dropzone-uploader/dist/styles.css';

const InputFileUploadSingleWithWebcam = ({
  extensiontype,
  description,
  form,
  field,
  enableWebcam = true,
  webcamProps = {},
}) => {
  const { t } = useTranslation();
  const { errors, setFieldValue } = form;
  const { name, value } = field;
  const projectId = useSelector(projectIdSelector);
  const tenantId = useSelector(tenantIdSelector);
  const photoValidation = usePhotoValidation();
  const renderDropzone = !value?.length;
  const maxFiles = 1;
  const noFileValue = '';
  const noFileNameValue = '';
  const [validationError, setValidationError] = useState('');
  const classes = useStyles(validationError)();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(value || noFileValue);
  const [uploadedFileName, setUploadedFileName] = useState(noFileNameValue);
  const [localBlobUrl, setLocalBlobUrl] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    setUploadedFile(value);
  }, [value]);

  // This component only renders for the facePhoto/Photo field type
  // (FILE_UPLOAD_SINGLE_WITH_WEBCAM), so every upload request must include the
  // isFacePhoto flag together with the current projectId and tenantId.
  const getFileUploadUrl = () => {
    const baseUrl = `${ENDPOINTS.FILE_UPLOAD_API.replace(
      ENDPOINTS_SEGMENTS.FILE_UPLOAD_DIRNAME,
      FILE_UPLOAD_DIRNAME,
    )}`;

    const params = new URLSearchParams({ isFacePhoto: 'true' });
    if (projectId) {
      params.append('projectId', projectId);
    }
    if (tenantId) {
      params.append('tenantId', tenantId);
    }

    return `${baseUrl}?${params.toString()}`;
  };

  const getUploadParams = () => {
    return {
      url: getFileUploadUrl(),
      method: 'post',
      headers: {},
    };
  };

  const updateValue = (fileUrl, fileName) => {
    setFieldValue(name, fileUrl);
    setUploadedFile(fileUrl);
    if (fileName) {
      setUploadedFileName(fileName);
    }
  };

  const setAcceptedFiles = (types) => {
    return types.map((typ) => `.${typ}`).join(', ');
  };

  const startUploadingProcess = () => {
    setIsUploading(true);
  };

  const endUploadingProcess = (isSuccess, message) => {
    setIsUploading(false);
    setValidationError(!isSuccess ? message : '');
  };

  // When the status is a failure,
  // the file might be still addedto the list of files in the dropzone
  // (it has a remove method)
  // in this case, we want to remove the file from the list
  const removeSelectedFileOnStatusChangeError = ({ remove } = {}) => {
    if (remove) {
      remove();
    }
  };

  const handleChangeStatus = (fileWithMeta, status) => {
    switch (status) {
      case FILE_UPLOAD_STATUSES.PREPARING: {
        startUploadingProcess();
        break;
      }
      case FILE_UPLOAD_STATUSES.REJECTED_FILE_TYPE: {
        removeSelectedFileOnStatusChangeError(fileWithMeta);
        endUploadingProcess(
          false,
          `${t('FILE_UPLOAD_REJECTED_FILE_TYPE')} ${setAcceptedFiles(extensiontype)}`,
        );
        break;
      }
      case FILE_UPLOAD_STATUSES.ERROR_FILE_SIZE: {
        removeSelectedFileOnStatusChangeError(fileWithMeta);
        endUploadingProcess(false, t('FILE_UPLOAD_ERROR_FILE_SIZE'));
        break;
      }
      case FILE_UPLOAD_STATUSES.REJECTED_MAX_FILES: {
        removeSelectedFileOnStatusChangeError(fileWithMeta);
        endUploadingProcess(false, t('FILE_UPLOAD_REJECTED_MAX_FILES'));
        break;
      }
      case FILE_UPLOAD_STATUSES.EXCEPTION_UPLOAD: {
        removeSelectedFileOnStatusChangeError(fileWithMeta);
        endUploadingProcess(false, t('AN_ERROR_OCCURED'));
        break;
      }

      case FILE_UPLOAD_STATUSES.DONE: {
        try {
          const response = JSON.parse(fileWithMeta.xhr?.response);
          const fileUrl = decodeURI(response?.location);
          // Store the local blob URL for preview
          const blobUrl =
            fileWithMeta.meta?.previewUrl ||
            (fileWithMeta.file ? URL.createObjectURL(fileWithMeta.file) : null);
          setLocalBlobUrl(blobUrl);
          endUploadingProcess(true, '');
          updateValue(fileUrl, fileWithMeta?.meta?.name);

          // WARN_ONLY: photo is stored; surface non-blocking warnings if present.
          // Prefer the local blob so the modal preview matches reject mode and
          // does not depend on a private S3 URL (which would 403 in <img>).
          photoValidation.handlePotentialValidationWarnings(
            response,
            blobUrl || fileUrl,
          );
        } catch (error) {
          removeSelectedFileOnStatusChangeError(fileWithMeta);
          endUploadingProcess(false, t('AN_ERROR_OCCURED'));
        }
        break;
      }
      default:
        break;
    }
  };

  const deleteFileHandler = async () => {
    if (uploadedFileName) {
      Api.delete(
        `${ENDPOINTS.FILE_UPLOAD_API.replace(
          ENDPOINTS_SEGMENTS.FILE_UPLOAD_DIRNAME,
          FILE_UPLOAD_DIRNAME,
        )}/${uploadedFileName}`,
        {},
      );
    }

    // Revoke the blob URL to free memory
    if (localBlobUrl && localBlobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(localBlobUrl);
    }
    setLocalBlobUrl(null);
    updateValue(noFileValue, noFileNameValue);
  };

  // For WARN_ONLY, "Replace Photo" clears the accepted upload so the user can
  // pick a new image. Enforce-mode rejections never stored a file.
  const handleReplacePhoto = () => {
    const shouldClearUpload =
      photoValidation.result?.validationMode === PHOTO_VALIDATION_MODE.WARN_ONLY;
    photoValidation.close();
    if (shouldClearUpload) {
      deleteFileHandler();
    }
  };

  // Shared handling for a failed upload. Intercepts photo validation failures
  // (HTTP 422) and shows the validation dialog instead of the generic error.
  // `previewUrl` is the local blob URL of the rejected image (owned by the
  // dialog once handed over).
  const handleUploadError = (error, previewUrl) => {
    if (
      error?.response?.status === PHOTO_VALIDATION_HTTP_STATUS &&
      photoValidation.handlePotentialValidationError(
        error.response.data,
        previewUrl,
      )
    ) {
      // The rejected photo was not stored; remove the local preview and stop
      // the spinner without surfacing the generic error message.
      setLocalBlobUrl(null);
      endUploadingProcess(true, '');
      return;
    }

    endUploadingProcess(false, t('AN_ERROR_OCCURED'));
  };

  const handleWebcamCapture = async (file) => {
    let blobUrl = null;
    try {
      startUploadingProcess();
      // Create local blob URL for preview
      blobUrl = URL.createObjectURL(file);
      setLocalBlobUrl(blobUrl);
      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', file);
      // Upload the captured image
      const response = await Api.post(getFileUploadUrl(), formData);
      const fileUrl = decodeURI(response?.location);
      endUploadingProcess(true, '');
      updateValue(fileUrl, file.name);

      // WARN_ONLY: photo is stored; surface non-blocking warnings if present.
      // Prefer the local blob so the modal preview matches reject mode and
      // does not depend on a private S3 URL (which would 403 in <img>).
      photoValidation.handlePotentialValidationWarnings(
        response,
        blobUrl || fileUrl,
      );
    } catch (error) {
      handleUploadError(error, blobUrl);
    }
  };

  const handleWebcamClose = () => {
    setShowWebcam(false);
  };

  const openWebcam = () => {
    setShowWebcam(true);
  };

  // Handle manual file input change
  const handleFileInputChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Validate file type
    const allowedTypes = extensiontype.map((typ) => typ.toLowerCase());
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExt)) {
      setValidationError(
        `${t('FILE_UPLOAD_REJECTED_FILE_TYPE')} ${setAcceptedFiles(extensiontype)}`,
      );
      return;
    }
    // Validate file size
    if (file.size > FILE_UPLOAD_MAX_SIZE_BYTES) {
      setValidationError(t('FILE_UPLOAD_ERROR_FILE_SIZE'));
      return;
    }
    setValidationError('');
    let blobUrl = null;
    try {
      startUploadingProcess();
      // Create local blob URL for preview
      blobUrl = URL.createObjectURL(file);
      setLocalBlobUrl(blobUrl);
      const formData = new FormData();
      formData.append('file', file);
      const response = await Api.post(getFileUploadUrl(), formData);
      const fileUrl = decodeURI(response?.location);
      endUploadingProcess(true, '');
      updateValue(fileUrl, file.name);

      // WARN_ONLY: photo is stored; surface non-blocking warnings if present.
      // Prefer the local blob so the modal preview matches reject mode and
      // does not depend on a private S3 URL (which would 403 in <img>).
      photoValidation.handlePotentialValidationWarnings(
        response,
        blobUrl || fileUrl,
      );
    } catch (error) {
      handleUploadError(error, blobUrl);
    }
  };

  // Upload progress indicator component
  const UploadProgress = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: 16,
      }}
    >
      <CircularProgress className={classes.dropZoneIcon} />
      <Typography className={classes.dropZoneLabel}>
        {t('FILE_UPLOAD_START_UPLOAD')}
      </Typography>
    </div>
  );

  useEffect(() => {
    if (errors[`${name}`] === ERROR_TYPES.FILE_UPLOAD_REQUIRED) {
      setValidationError(t('FILE_UPLOAD_REQUIRED'));
    }
  }, [errors, name, t]);

  return (
    <div className={classes.dropZoneContainer}>
      <Typography
        dangerouslySetInnerHTML={{ __html: sanitizer(description) }}
        className={classes.dropZoneDescription}
      />
      {/* Show upload progress when uploading */}
      {isUploading && <UploadProgress />}
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={setAcceptedFiles(extensiontype)}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      {/* Default View - Two Buttons */}
      {renderDropzone && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 16,
            marginBottom: 16,
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            alignItems: 'center',
          }}
        >
          <Button
            label={t('UPLOAD_PHOTO_BUTTON')}
            variant="primary"
            icon={<BackupIcon />}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          />
          {enableWebcam && (
            <Button
              label={t('TAKE_PHOTO_BUTTON')}
              variant="primary"
              icon={<CameraAltIcon />}
              onClick={openWebcam}
            />
          )}
        </div>
      )}
      <PhotoRequirementsTrigger />
      {validationError && (
        <Typography
          dangerouslySetInnerHTML={{ __html: sanitizer(validationError) }}
          className={classes.dropZoneError}
        />
      )}
      {uploadedFile && (
        <>
          <Typography
            dangerouslySetInnerHTML={{
              __html: sanitizer(t('FILE_UPLOAD_PREVIEW')),
            }}
            className={classes.dropZoneDescription}
          />
          <FilePreview
            file={uploadedFile}
            localBlobUrl={localBlobUrl}
            deleteFile={deleteFileHandler}
          />
        </>
      )}
      {/* Webcam Capture Dialog */}
      {enableWebcam && (
        <WebcamCapture
          extensiontype={extensiontype}
          open={showWebcam}
          onClose={handleWebcamClose}
          onCapture={handleWebcamCapture}
          aspectRatio={1}
          enableCrop={true}
          maxWidth={1280}
          maxHeight={720}
          quality={0.8}
          {...webcamProps}
        />
      )}

      <PhotoValidationModal
        open={photoValidation.isOpen}
        result={photoValidation.result}
        previewUrl={photoValidation.previewUrl}
        onReplacePhoto={handleReplacePhoto}
        onClose={photoValidation.close}
      />
    </div>
  );
};

InputFileUploadSingleWithWebcam.propTypes = {
  extensiontype: PropTypes.array.isRequired,
  required: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  enableWebcam: PropTypes.bool,
  webcamProps: PropTypes.object,
};

export default InputFileUploadSingleWithWebcam;
